/**
 * Employee Separations — list + create.
 *
 * Route: /hr/separations
 *
 * Master view: separation requests across all states, filterable by
 * status + type. Click a row → opens detail with 6 tabs.
 *
 * "New Separation" drawer captures: employee + type + dates + notice +
 * reason. Hard-exit types (ABSCONDING / TERMINATION / DEATH) bypass
 * notice rules — UI surfaces this with a yellow alert.
 */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Button, Card, Col, DatePicker, Drawer, Form, Input, InputNumber,
  Modal, Row, Select, Space, Statistic, Table, Tag, Typography, message,
} from 'antd'
import {
  PlusOutlined, ReloadOutlined, UserDeleteOutlined, WarningOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'

import {
  separationApi, SEPARATION_TYPES, STATUS_COLOR, TYPE_COLOR,
  type SeparationListRow, type SeparationStatus, type SeparationType,
} from '@/features/hr/separationApi'
import { employeesApi } from '@/features/hr/api'

const { Title, Text } = Typography

const HARD_EXIT = new Set(['ABSCONDING', 'TERMINATION', 'DEATH'])

const REASON_OPTIONS = [
  'Better opportunity', 'Salary issue', 'Personal reason', 'Family reason',
  'Health issue', 'Relocation', 'Higher studies', 'Work pressure',
  'Supervisor issue', 'Shift issue', 'Accommodation issue', 'Transport issue',
  'Retirement age', 'Disciplinary', 'Contract completed', 'Other',
]

export default function SeparationListPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<SeparationStatus | 'OPEN' | undefined>('OPEN')
  const [typeFilter, setTypeFilter] = useState<SeparationType | undefined>()
  const [createOpen, setCreateOpen] = useState(false)
  const [form] = Form.useForm()
  const [selectedType, setSelectedType] = useState<SeparationType | undefined>()

  const summaryQ = useQuery({
    queryKey: ['separation-summary'],
    queryFn: separationApi.summary,
    refetchInterval: 60_000,
  })

  const listQ = useQuery({
    queryKey: ['separations', statusFilter, typeFilter],
    queryFn: () => separationApi.list({
      status: statusFilter && statusFilter !== 'OPEN' ? statusFilter : undefined,
      separation_type: typeFilter,
      limit: 500,
    }),
  })

  // Employee dropdown for the create form. Pull active employees only.
  const empListQ = useQuery({
    queryKey: ['employees-for-separation'],
    queryFn: () => employeesApi.list({ page: 1, size: 1000, is_active: true }),
    enabled: createOpen,
  })

  const filtered = useMemo(() => {
    const rows = listQ.data ?? []
    if (statusFilter === 'OPEN') {
      const closed = new Set(['CLOSED', 'CANCELLED', 'REJECTED', 'PAID'])
      return rows.filter((r) => !closed.has(r.current_status))
    }
    return rows
  }, [listQ.data, statusFilter])

  const createMut = useMutation({
    mutationFn: (vals: any) => separationApi.create({
      employee_id: vals.employee_id,
      separation_type: vals.separation_type,
      resignation_date: vals.resignation_date
        ? dayjs(vals.resignation_date).format('YYYY-MM-DD') : undefined,
      requested_last_working_date: vals.requested_last_working_date
        ? dayjs(vals.requested_last_working_date).format('YYYY-MM-DD') : undefined,
      notice_period_days_required: vals.notice_period_days_required,
      reason_for_leaving: vals.reason_for_leaving,
      detailed_remarks: vals.detailed_remarks,
    }),
    onSuccess: (req) => {
      message.success(`Separation created — ${req.separation_no}`)
      qc.invalidateQueries({ queryKey: ['separations'] })
      qc.invalidateQueries({ queryKey: ['separation-summary'] })
      setCreateOpen(false)
      form.resetFields()
      setSelectedType(undefined)
      navigate(`/hr/separations/${req.id}`)
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Create failed'),
  })

  const isHardExit = selectedType && HARD_EXIT.has(selectedType)

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ justifyContent: 'space-between', width: '100%', marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>Employee Separations</Title>
          <Text type="secondary">
            Resignation / Termination / Retirement / Absconding workflow with full HR → Finance → Management approval chain and F&F settlement.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => { form.resetFields(); setCreateOpen(true) }}
        >
          New Separation
        </Button>
      </Space>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6} md={4}>
          <Card size="small">
            <Statistic title="Total" value={summaryQ.data?.total ?? '—'} />
          </Card>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Card size="small">
            <Statistic title="Open" value={summaryQ.data?.open ?? '—'}
              valueStyle={{ color: '#fa8c16' }} prefix={<UserDeleteOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Card size="small">
            <Statistic title="Paid" value={summaryQ.data?.paid ?? '—'}
              valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Card size="small">
            <Statistic title="Closed" value={summaryQ.data?.closed ?? '—'} />
          </Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Text>Status:</Text>
          <Select
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            style={{ width: 220 }}
            allowClear
            options={[
              { label: 'Open (non-terminal)', value: 'OPEN' },
              { label: '— All —', value: undefined },
              ...['DRAFT', 'SUBMITTED', 'MANAGER_REVIEW', 'HR_REVIEW',
                  'NOTICE_PERIOD_RUNNING', 'HANDOVER_PENDING', 'NO_DUES_PENDING',
                  'PAYROLL_PENDING', 'FINANCE_REVIEW', 'FNF_PENDING_APPROVAL',
                  'FNF_APPROVED', 'PAYMENT_PENDING', 'PAID', 'CLOSED',
                  'HOLD', 'CANCELLED', 'REJECTED'].map((s) => ({ label: s, value: s })),
            ]}
          />
          <Text>Type:</Text>
          <Select
            allowClear
            value={typeFilter}
            onChange={setTypeFilter}
            placeholder="Any"
            style={{ width: 180 }}
            options={SEPARATION_TYPES.map((t) => ({ label: t, value: t }))}
          />
          <Button icon={<ReloadOutlined />}
            onClick={() => qc.invalidateQueries({ queryKey: ['separations'] })}>
            Refresh
          </Button>
        </Space>
      </Card>

      <Card>
        <Table<SeparationListRow>
          dataSource={filtered}
          rowKey="id"
          loading={listQ.isLoading}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          size="small"
          onRow={(r) => ({
            onClick: () => navigate(`/hr/separations/${r.id}`),
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: 'No.', dataIndex: 'separation_no', width: 130,
              render: (v) => <Text code>{v}</Text>,
            },
            {
              title: 'Employee', key: 'emp', width: 240,
              render: (_, r) => (
                <Space direction="vertical" size={0}>
                  <Text strong>{r.emp_name ?? '—'}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {r.employee_code} · {r.department_name ?? 'no dept'}
                  </Text>
                </Space>
              ),
            },
            {
              title: 'Type', dataIndex: 'separation_type', width: 130,
              render: (v: SeparationType, r) => (
                <Space direction="vertical" size={0}>
                  <Tag color={TYPE_COLOR[v]}>{v}</Tag>
                  {r.is_hard_exit && <Tag color="red" style={{ fontSize: 10 }}>hard-exit</Tag>}
                </Space>
              ),
            },
            {
              title: 'Resignation', dataIndex: 'resignation_date', width: 110,
              render: (v) => v ? dayjs(v).format('DD MMM YY') : '—',
            },
            {
              title: 'Approved LWD', dataIndex: 'approved_last_working_date', width: 120,
              render: (v) => v
                ? <Text strong>{dayjs(v).format('DD MMM YY')}</Text>
                : <Text type="secondary">pending</Text>,
            },
            {
              title: 'Status', dataIndex: 'current_status', width: 180,
              render: (v: SeparationStatus, r) => (
                <Space direction="vertical" size={0}>
                  <Tag color={STATUS_COLOR[v]}>{v.replace(/_/g, ' ')}</Tag>
                  {r.final_settlement_status && (
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      F&F: {r.final_settlement_status}
                    </Text>
                  )}
                </Space>
              ),
            },
            {
              title: 'Created', dataIndex: 'created_at', width: 110,
              render: (v) => v ? dayjs(v).format('DD MMM YY') : '—',
            },
          ]}
        />
      </Card>

      <Drawer
        open={createOpen}
        title="New Separation Request"
        width={520}
        onClose={() => { setCreateOpen(false); form.resetFields(); setSelectedType(undefined) }}
        footer={
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button type="primary" loading={createMut.isPending}
              onClick={() => form.validateFields().then((v) => createMut.mutate(v))}>
              Create
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="employee_id"
            label="Employee"
            rules={[{ required: true, message: 'Pick an employee' }]}
          >
            <Select
              showSearch
              placeholder="Search by name or code"
              loading={empListQ.isLoading}
              filterOption={(input, option) =>
                (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={(empListQ.data?.items ?? []).map((e) => ({
                label: `${e.emp_code} — ${e.emp_name}`,
                value: e.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="separation_type"
            label="Separation Type"
            rules={[{ required: true, message: 'Pick a type' }]}
          >
            <Select
              placeholder="What kind of exit?"
              options={SEPARATION_TYPES.map((t) => ({ label: t.replace(/_/g, ' '), value: t }))}
              onChange={(v) => setSelectedType(v)}
            />
          </Form.Item>

          {isHardExit && (
            <Alert
              type="warning" showIcon
              icon={<WarningOutlined />}
              style={{ marginBottom: 16 }}
              message={`${selectedType} is a hard-exit`}
              description="Notice period + handover validation will be skipped. F&F can be calculated immediately. Resignation date / requested LWD are optional."
            />
          )}

          <Form.Item name="resignation_date" label="Resignation Date">
            <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
          </Form.Item>
          <Form.Item name="requested_last_working_date" label="Requested Last Working Date">
            <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
          </Form.Item>
          <Form.Item
            name="notice_period_days_required"
            label="Notice Period (days)"
            initialValue={isHardExit ? 0 : 30}
            tooltip="Default 30 days. Hard-exits default to 0."
          >
            <InputNumber min={0} max={180} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="reason_for_leaving" label="Reason for leaving">
            <Select
              allowClear
              showSearch
              placeholder="Pick or type"
              options={REASON_OPTIONS.map((r) => ({ label: r, value: r }))}
            />
          </Form.Item>
          <Form.Item name="detailed_remarks" label="Detailed Remarks">
            <Input.TextArea rows={3} placeholder="Background context, anything HR needs to know" />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
